import { writable, get } from 'svelte/store';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import type { Device } from 'src/types/Device';
import type { MediaResponseInterface } from 'src/types/MediaResponseInterface';
import { real_id } from 'src/helpers/interface_helpers';

const radioFavoritesCache = writable<Record<string, boolean>>({});

/**
 * @description
 * Updates the radio favorites cache for a specific device
 * @param {string} deviceId - The device ID to update in the cache
 * @param {boolean} hasFavorites - Whether the device has radio favorites
 * @returns {void}
 */
export const setRadioFavoritesCache = (deviceId: string, hasFavorites: boolean) => {
	radioFavoritesCache.update((cache) => ({
		...cache,
		[real_id(deviceId)]: hasFavorites,
	}));
};

/**
 * @description
 * Checks if a device has radio favorites cached
 * @param {string} deviceId - The device ID to check
 * @returns {boolean | undefined} Returns true if device has favorites, false if it doesn't, or undefined if not cached
 */
export const hasRadioFavorites = (deviceId: string): boolean | undefined => {
	const cache = get(radioFavoritesCache);
	return cache[real_id(deviceId)];
};

/**
 * @description
 * Checks if a wall display media player device has saved radio favorites by fetching from the device if not cached
 * @param {Device} device - The device to check for radio favorites
 * @returns {Promise<boolean>} Returns true if device has radio favorites, false otherwise
 */
export const checkRadioFavorites = async (device: Device): Promise<boolean> => {
	const deviceId = real_id(device.id);
	const cached = hasRadioFavorites(deviceId);
	if (cached !== undefined) {
		return cached;
	}

	const channel = device.channel;
	const data = channel ? { config: { channel } } : undefined;

	try {
		const res: MediaResponseInterface = await gen2Request(device, 'Media.Radio.ListFavourites', {
			...data,
		});

		if (res?.isok) {
			let radioFavorites: any[] = [];
			if (res?.data?.list?.length > 0) {
				radioFavorites = res.data.list;
			} else if (res?.list?.length > 0) {
				radioFavorites = res.list;
			}

			const hasFavorites = radioFavorites.length > 0;
			setRadioFavoritesCache(deviceId, hasFavorites);
			return hasFavorites;
		}
	} catch (error) {
		console.error('Error checking radio favorites:', error);
	}

	return false;
};
